import { useState } from 'react';

type Status = 'connecting' | 'open' | 'closed';

/**
 * Always-available "reconnect with the same parameters" control, pinned to the
 * top of the screen. On phones the socket silently drops after a lock/background;
 * this gives the player a one-tap way to force a fresh connection, and shows the
 * live connection status so they know when it's back.
 */
export function ReconnectButton({ status, onReconnect }: { status: Status; onReconnect: () => void }) {
  const [spinning, setSpinning] = useState(false);

  const handle = () => {
    onReconnect();
    setSpinning(true);
    window.setTimeout(() => setSpinning(false), 1200);
  };

  const label =
    status === 'open' ? 'На связи' : status === 'connecting' ? 'Подключаемся…' : 'Нет связи';

  return (
    <div
      style={{
        position: 'fixed',
        top: 'max(8px, env(safe-area-inset-top))',
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 300,
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '6px 10px 6px 12px',
        borderRadius: 'var(--radius-pill)',
        background: 'var(--bg2)',
        border: '1px solid var(--line-strong)',
        boxShadow: '0 6px 20px rgba(0,0,0,.28)',
        fontSize: 13,
        color: 'var(--ink)',
        maxWidth: 'calc(100vw - 24px)',
      }}
    >
      <span
        style={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          background: status === 'open' ? '#4caf50' : status === 'connecting' ? '#e0b34f' : '#e05a4f',
          flexShrink: 0,
        }}
      />
      <span style={{ color: 'var(--ink-dim)', whiteSpace: 'nowrap' }}>{label}</span>
      <button
        type="button"
        onClick={handle}
        disabled={spinning}
        style={{
          appearance: 'none',
          border: '1px solid var(--line-strong)',
          background: 'transparent',
          color: 'var(--ink)',
          fontFamily: 'inherit',
          fontSize: 13,
          fontWeight: 600,
          padding: '4px 10px',
          borderRadius: 'var(--radius-pill)',
          cursor: spinning ? 'default' : 'pointer',
          opacity: spinning ? 0.6 : 1,
          whiteSpace: 'nowrap',
        }}
      >
        {spinning ? '↻…' : '↻ Переподключиться'}
      </button>
    </div>
  );
}
